import { useSyncExternalStore } from 'react'

/* ===========================================================================
 *  Licht en donker
 *
 *  Dezelfde drie standen als het dashboard (src/lib/theme.ts daar), met
 *  dezelfde sleutel in de opslag: tw.thema. Het zijn twee adressen, dus de
 *  keuze reist niet vanzelf mee -- maar wie in het dashboard "volg het
 *  systeem" heeft, krijgt hier hetzelfde zonder iets te kiezen, en dat is
 *  bijna iedereen.
 * =========================================================================== */

export type ThemaKeuze = 'systeem' | 'licht' | 'donker'

export const THEMA_LABELS: Record<ThemaKeuze, string> = {
  systeem: 'Volg het systeem',
  licht: 'Licht',
  donker: 'Donker',
}

const SLEUTEL = 'tw.thema'

function leesKeuze(): ThemaKeuze {
  try {
    const w = localStorage.getItem(SLEUTEL)
    return w === 'licht' || w === 'donker' ? w : 'systeem'
  } catch {
    return 'systeem'
  }
}

const systeem = () => {
  try { return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'donker' : 'licht' } catch { return 'donker' }
}

let keuze: ThemaKeuze = leesKeuze()
const luisteraars = new Set<() => void>()

export function actiefThema(): 'licht' | 'donker' {
  return keuze === 'systeem' ? systeem() : keuze
}

function pasToe() {
  const t = actiefThema()
  document.documentElement.dataset.thema = t
  document.documentElement.style.colorScheme = t === 'donker' ? 'dark' : 'light'
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', t === 'donker' ? '#070c16' : '#ffffff')
  for (const l of luisteraars) l()
}

export function kiesThema(k: ThemaKeuze) {
  keuze = k
  try {
    if (k === 'systeem') localStorage.removeItem(SLEUTEL)
    else localStorage.setItem(SLEUTEL, k)
  } catch { /* privévenster: dan geldt het alleen voor deze keer */ }
  pasToe()
}

/** Eén keer bij het opstarten, vóór de eerste render. */
export function startThema() {
  pasToe()
  try {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (keuze === 'systeem') pasToe()
    })
  } catch { /* oude browser: dan volgt hij het systeem pas na verversen */ }
}

export function useThema(): { keuze: ThemaKeuze; actief: 'licht' | 'donker' } {
  const actief = useSyncExternalStore(
    (l) => { luisteraars.add(l); return () => { luisteraars.delete(l) } },
    actiefThema,
  )
  return { keuze, actief }
}
